import { apiClient } from './client';
import type {
  EventCardDto,
  EventDetailDto,
  EventRegistrationStatusDto,
  FeaturedEventResponse,
  PaginatedEventsResponse,
} from '@/lib/types/events.types';

/** Fetch the current featured event for the authenticated member. Returns null when the queue is empty. */
export async function getFeaturedEvent(): Promise<FeaturedEventResponse | null> {
  const { data } = await apiClient.get<FeaturedEventResponse | null>('/events/featured');
  return data;
}

/** Fetch the authenticated member's upcoming registered events. */
export async function getRegisteredEvents(): Promise<EventCardDto[]> {
  const { data } = await apiClient.get<EventCardDto[]>('/events/registered');
  return data;
}

/** Fetch a paginated page of upcoming non-private published events (page size 9). */
export async function getUpcomingEvents(page: number): Promise<PaginatedEventsResponse> {
  const { data } = await apiClient.get<PaginatedEventsResponse>('/events/upcoming', {
    params: { page },
  });
  return data;
}

/**
 * Search events by title/description. Pass includePast=true to include events whose
 * startsAt is in the past. An empty q must not be sent — callers must guard on their end.
 */
export async function searchEvents(
  q: string,
  includePast: boolean,
  page: number,
): Promise<PaginatedEventsResponse> {
  const { data } = await apiClient.get<PaginatedEventsResponse>('/events/search', {
    params: { q, includePast, page },
  });
  return data;
}

/** Fetch full event detail for any event by id, including private events. */
export async function getEventDetail(id: string): Promise<EventDetailDto> {
  const { data } = await apiClient.get<EventDetailDto>(`/events/${id}`);
  return data;
}

/**
 * Check whether the authenticated caller is registered for an event.
 * Backed by GET /v1/events/:eventId/register — requires auth. For a registered
 * member on an open event the answer also carries the join details (FR-292).
 */
export async function getEventRegistrationStatus(eventId: string): Promise<EventRegistrationStatusDto> {
  const { data } = await apiClient.get<EventRegistrationStatusDto>(`/events/${eventId}/register`);
  return data;
}

/** Register the authenticated user for an event (FR-62/FR-63, MYN-52). */
export async function registerForEvent(eventId: string): Promise<void> {
  await apiClient.post(`/events/${eventId}/register`);
}

/** Unregister the authenticated user from an event (FR-62/FR-65, MYN-52). */
export async function unregisterFromEvent(eventId: string): Promise<void> {
  await apiClient.delete(`/events/${eventId}/register`);
}
